import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Save, Plus, X, Upload, Image as ImageIcon, RotateCw, ZoomIn, ZoomOut, Loader2, MapPin, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { createActivity, updateActivity } from '../../services/api';
import { toast } from 'sonner';
import axios from 'axios';
import { getCategories } from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ActivityForm = ({ activity, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: 'Benessere',
    zone: 'Roma',
    address: '',
    phone: '',
    website: '',
    discount: '',
    description: '',
    products: [],
    image: '',
    gallery: [],
    featured: false
  });
  const [newProduct, setNewProduct] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Stati per immagini copertina
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [imageRotation, setImageRotation] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);

  // Stati per immagini gallery
  const [uploadingGallery, setUploadingGallery] = useState(false);

  // --- LOGICA AUTOCOMPLETE CITTA' ---
  const [citySearch, setCitySearch] = useState(''); 
  const [cities, setCities] = useState([]); 
  const [filteredCities, setFilteredCities] = useState([]); 
  const [showSuggestions, setShowSuggestions] = useState(false); 
  const [loadingCities, setLoadingCities] = useState(false); 
  const wrapperRef = useRef(null); 

  const [categoriesList, setCategoriesList] = useState([]);
  
  // Fetch categorie all'avvio
  useEffect(() => {
    const loadCats = async () => {
      try {
        const data = await getCategories();
        setCategoriesList(data.map(c => c.name));
      } catch (err) {
        console.error(err);
      }
    };
    loadCats();
  }, []);

  // 1. Fetch Iniziale Città
  useEffect(() => {
    const fetchCities = async () => {
      setLoadingCities(true);
      try {
        const response = await fetch('https://raw.githubusercontent.com/matteocontrini/comuni-json/master/comuni.json');
        const data = await response.json();
        const cityNames = data.map(c => c.nome);
        setCities(cityNames);
      } catch (error) {
        console.error("Errore caricamento comuni:", error);
        setCities(['Roma', 'Milano', 'Napoli', 'Torino', 'Palermo', 'Firenze']); // Fallback
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, []);

  // 2. Setup dati iniziali (Edit Mode)
  useEffect(() => {
    if (activity) {
      setFormData({
        name: activity.name || '',
        category: activity.category || 'Benessere',
        zone: activity.zone || 'Roma',
        address: activity.address || '',
        phone: activity.phone || '',
        website: activity.website || '',
        discount: activity.discount || '',
        description: activity.description || '',
        products: activity.products || [],
        image: activity.image || '',
        gallery: activity.gallery || [], 
        featured: activity.featured || false
      });
      setImagePreview(activity.image || '');
      setImageRotation(0);
      setImageZoom(1);
      setCitySearch(activity.zone || 'Roma');
    }
  }, [activity]);

  // 3. Filtro dinamico
  useEffect(() => {
    if (citySearch.length > 1) {
      const results = cities.filter(city =>
        city.toLowerCase().startsWith(citySearch.toLowerCase())
      );
      setFilteredCities(results.slice(0, 50)); 
      setShowSuggestions(true);
    } else {
      setFilteredCities([]);
      setShowSuggestions(false);
    }
  }, [citySearch, cities]);

  // 4. Chiudi tendina se clicco fuori
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectCity = (city) => {
    setCitySearch(city);
    setFormData(prev => ({ ...prev, zone: city }));
    setShowSuggestions(false);
  };

  const handleAddProduct = () => {
    if (newProduct.trim()) {
      setFormData(prev => ({
        ...prev,
        products: [...prev.products, newProduct.trim()]
      }));
      setNewProduct('');
    }
  };

  const handleRemoveProduct = (index) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  // Upload Immagine Copertina (CORRETTO con TOKEN)
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Formato file non valido. Usa JPG, PNG o WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Dimensione massima: 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      // Recuperiamo il token per l'autenticazione
      const token = localStorage.getItem('admin_token');

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}` // Header mancante aggiunto
        }
      });

      const imageUrl = response.data.url;
      setFormData(prev => ({ ...prev, image: imageUrl }));
      setImagePreview(imageUrl);
      toast.success('Immagine caricata con successo!');
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error.response && error.response.status === 401) {
          toast.error('Sessione scaduta o non autorizzata. Effettua nuovamente il login.');
      } else if (error.response && error.response.status === 413) {
        toast.error('File troppo grande per il server (Max upload size)');
      } else if (error.response && error.response.status === 500) {
        toast.error('Errore durante l\'ottimizzazione. Riprova con un\'immagine più piccola.');
      } else {
        toast.error('Errore di connessione o timeout. Riprova.');
      }
    } finally {
      setUploadingImage(false);
    }
  };

  // Upload Immagini Gallery (CORRETTO con TOKEN)
  const handleGalleryUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (formData.gallery.length >= 5) {
        toast.error("Hai raggiunto il limite di 5 immagini aggiuntive.");
        return;
    }

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato file non valido. Usa JPG, PNG o WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande (Max 5MB)');
      return;
    }

    setUploadingGallery(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      // Recuperiamo il token per l'autenticazione
      const token = localStorage.getItem('admin_token');

      const response = await axios.post(`${API_URL}/api/upload/image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}` // Header mancante aggiunto
        }
      });

      const imageUrl = response.data.url;
      setFormData(prev => ({ 
          ...prev, 
          gallery: [...prev.gallery, imageUrl] 
      }));
      toast.success('Immagine aggiunta alla galleria!');
    } catch (error) {
      console.error('Gallery upload error:', error);
      if (error.response && error.response.status === 401) {
          toast.error('Sessione scaduta. Effettua il login.');
      } else {
          toast.error('Errore caricamento immagine');
      }
    } finally {
        setUploadingGallery(false);
        e.target.value = null; // Reset input
    }
  };

  // Rimuovi Immagine Gallery
  const removeGalleryImage = (indexToRemove) => {
      setFormData(prev => ({
          ...prev,
          gallery: prev.gallery.filter((_, index) => index !== indexToRemove)
      }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image) {
      toast.error('Carica un\'immagine per l\'attività');
      return;
    }
    
    if (!formData.zone) {
        toast.error('Seleziona una città valida');
        return;
    }

    setLoading(true);

    try {
      if (activity) {
        await updateActivity(activity.id, formData);
        toast.success('Attività aggiornata con successo!');
      } else {
        await createActivity(formData);
        toast.success('Attività creata con successo!');
      }
      onClose();
    } catch (error) {
      console.error('Error saving activity:', error);
      toast.error('Errore nel salvataggio dell\'attività');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {activity ? 'Modifica Attività' : 'Nuova Attività'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <Label htmlFor="name">Nome Attività *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Es: Pizzeria La Margherita"
                  required
                />
              </div>

              {/* Category & City */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Categoria *</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="w-full h-10 px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:border-gray-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                    required
                  >
                    {categoriesList.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Categoria dell'attività</p>
                </div>
                
                {/* --- AUTOCOMPLETE CITY INPUT --- */}
                <div ref={wrapperRef} className="relative">
                  <Label htmlFor="city">Città *</Label>
                  <div className="relative mt-1">
                    <div className="absolute left-3 top-2.5 text-gray-400">
                        {loadingCities ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                    </div>
                    <Input
                      id="city"
                      value={citySearch}
                      onChange={(e) => {
                          setCitySearch(e.target.value);
                          handleChange('zone', e.target.value);
                      }}
                      onFocus={() => citySearch.length > 1 && setShowSuggestions(true)}
                      placeholder="Inizia a digitare... (es: Milano)"
                      className="pl-9"
                      required
                      autoComplete="off"
                    />
                  </div>
                  
                  {/* TENDINA */}
                  {showSuggestions && filteredCities.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white text-left text-gray-800 rounded-md shadow-xl border border-gray-200 max-h-60 overflow-y-auto z-50">
                        {filteredCities.map((city, index) => (
                        <div 
                            key={index}
                            onClick={() => handleSelectCity(city)}
                            className="px-4 py-2 hover:bg-teal-50 cursor-pointer transition-colors text-sm border-b border-gray-50 last:border-0"
                        >
                            {city}
                        </div>
                        ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Seleziona la città dalla lista per evitare errori.
                  </p>
                </div>
              </div>

              {/* Address */}
              <div>
                <Label htmlFor="address">Indirizzo *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Via Roma 100, 50053 Empoli (FI)"
                  required
                />
              </div>

              {/* Phone & Website */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Telefono *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+39 0571 123456"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="website">Sito Web</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="www.esempio.it"
                  />
                </div>
              </div>

              {/* Discount */}
              <div>
                <Label htmlFor="discount">Sconto *</Label>
                <Input
                  id="discount"
                  value={formData.discount}
                  onChange={(e) => handleChange('discount', e.target.value)}
                  placeholder="Es: 20% su tutti i prodotti"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Descrizione *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Descrivi l'attività..."
                  rows={4}
                  required
                />
              </div>

              {/* Products */}
              <div>
                <Label>Prodotti/Servizi con Sconto</Label>
                <div className="space-y-2 mt-2">
                  {formData.products.map((product, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-50 px-3 py-2 rounded-lg text-sm">
                        {product}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleRemoveProduct(index)}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex space-x-2">
                    <Input
                      value={newProduct}
                      onChange={(e) => setNewProduct(e.target.value)}
                      placeholder="Aggiungi prodotto/servizio"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddProduct())}
                    />
                    <Button
                      type="button"
                      onClick={handleAddProduct}
                      variant="outline"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* --- GESTIONE IMMAGINI --- */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Gestione Immagini</h3>

                {/* 1. Immagine Copertina */}
                <div className="mb-6">
                    <Label htmlFor="image" className="text-base font-medium">Immagine di Copertina (Principale) *</Label>
                    <div className="mt-2 space-y-4">
                    <div className="flex items-center space-x-4">
                        <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('imageUpload').click()}
                        disabled={uploadingImage}
                        className="flex items-center space-x-2"
                        >
                        {uploadingImage ? (
                            <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Ottimizzazione in corso...</span>
                            </>
                        ) : (
                            <>
                            <Upload className="w-4 h-4" />
                            <span>{formData.image ? 'Sostituisci Copertina' : 'Carica Copertina'}</span>
                            </>
                        )}
                        </Button>
                        <input
                        id="imageUpload"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        />
                        <span className="text-sm text-gray-500">JPG, PNG o WebP (max 5MB)</span>
                    </div>

                    {imagePreview ? (
                        <div className="space-y-3">
                        <div className="relative rounded-lg overflow-hidden border-2 border-teal-500 bg-gray-100 max-w-md">
                            <img
                            src={imagePreview}
                            alt="Preview"
                            className="w-full h-48 object-cover transition-transform duration-300"
                            style={{
                                transform: `rotate(${imageRotation}deg) scale(${imageZoom})`
                            }}
                            onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/800x600?text=Immagine+Non+Disponibile';
                            }}
                            />
                            <div className="absolute top-2 right-2 flex space-x-2">
                                <span className="bg-black/50 text-white text-xs px-2 py-1 rounded">Copertina</span>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => {
                                    setFormData(prev => ({ ...prev, image: '' }));
                                    setImagePreview('');
                                    setImageRotation(0);
                                    setImageZoom(1);
                                    }}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        
                        {/* Image Controls */}
                        <div className="flex items-center space-x-2 bg-gray-50 rounded-lg p-2 max-w-md">
                            <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setImageRotation((prev) => prev - 90)}
                            title="Ruota a sinistra"
                            >
                            <RotateCw className="w-4 h-4 rotate-180" />
                            </Button>
                            <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setImageRotation((prev) => prev + 90)}
                            title="Ruota a destra"
                            >
                            <RotateCw className="w-4 h-4" />
                            </Button>
                            <div className="w-px h-6 bg-gray-300" />
                            <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setImageZoom((prev) => Math.max(0.5, prev - 0.1))}
                            title="Riduci zoom"
                            disabled={imageZoom <= 0.5}
                            >
                            <ZoomOut className="w-4 h-4" />
                            </Button>
                            <span className="text-sm text-gray-600 w-12 text-center">
                            {Math.round(imageZoom * 100)}%
                            </span>
                            <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setImageZoom((prev) => Math.min(2, prev + 0.1))}
                            title="Aumenta zoom"
                            disabled={imageZoom >= 2}
                            >
                            <ZoomIn className="w-4 h-4" />
                            </Button>
                            <div className="w-px h-6 bg-gray-300" />
                            <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setImageRotation(0);
                                setImageZoom(1);
                            }}
                            title="Reset"
                            >
                            Reset
                            </Button>
                        </div>
                        </div>
                    ) : (
                        <div className="w-full h-32 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed border-gray-300">
                        <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                        <p className="text-gray-500 text-sm">Nessuna copertina caricata</p>
                        </div>
                    )}
                    </div>
                </div>

                {/* 2. Galleria Immagini */}
                <div>
                    <Label className="text-base font-medium flex justify-between">
                        <span>Galleria Immagini Aggiuntive</span>
                        <span className="text-sm font-normal text-gray-500">{formData.gallery.length} / 5</span>
                    </Label>
                    <p className="text-sm text-gray-500 mb-3">Aggiungi altre foto dell'attività (interni, prodotti, staff...).</p>
                    
                    {/* Bottone Upload Gallery */}
                    {formData.gallery.length < 5 && (
                        <div className="mb-4">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => document.getElementById('galleryUpload').click()}
                                disabled={uploadingGallery}
                                className="w-full sm:w-auto"
                            >
                                {uploadingGallery ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                                Aggiungi Foto alla Galleria
                            </Button>
                            <input
                                id="galleryUpload"
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                                onChange={handleGalleryUpload}
                                className="hidden"
                            />
                        </div>
                    )}

                    {/* Griglia Gallery */}
                    {formData.gallery.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            {formData.gallery.map((imgUrl, idx) => (
                                <div key={idx} className="relative group rounded-lg overflow-hidden border border-gray-200 bg-white aspect-square">
                                    <img src={imgUrl} alt={`Gallery ${idx}`} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <Button 
                                            type="button" 
                                            variant="destructive" 
                                            size="icon"
                                            onClick={() => removeGalleryImage(idx)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="p-4 border border-dashed border-gray-200 rounded-lg text-center text-sm text-gray-400">
                            Nessuna immagine aggiuntiva
                        </div>
                    )}
                </div>
              </div>

              {/* Featured */}
              <div className="flex items-center space-x-2 pt-4 border-t">
                <Switch
                  id="featured"
                  checked={formData.featured}
                  onCheckedChange={(checked) => handleChange('featured', checked)}
                />
                <Label htmlFor="featured">In Evidenza</Label>
              </div>

              {/* Submit */}
              <div className="flex space-x-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  disabled={loading}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Salvataggio...' : 'Salva Attività'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Annulla
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};